// =============================================================================
// pmoCharts — the small, themed chart pieces the PMO screens share (recharts)
// 22 Oct 2026. James: "some more visual widgets and graphs, not just text in
// boxes … polish to catch the client's eye when presenting."
// =============================================================================

import { Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis, LabelList } from "recharts";

export const PALETTE = { accent: "#ff6a1a", ok: "#5ebf73", warn: "#e8b547", danger: "#e5484d", info: "#6ca3e5", muted: "#6b6b66", dim: "#3a3a40", purple: "#a78bfa", teal: "#2dd4bf" };
export const SERIES = [PALETTE.accent, PALETTE.info, PALETTE.ok, PALETTE.purple, PALETTE.teal, PALETTE.warn, PALETTE.danger];
export const TIP = { background: "#14161a", border: "1px solid #2a2e35", borderRadius: 6, fontSize: 12, padding: "6px 10px", color: "#eee" } as const;

/** A ring with one big number in the middle — win rate, ack rate, readiness. */
export function Ring({ value, label, sub, colour = PALETTE.accent, size = 120, suffix = "%" }: { value: number | null; label: string; sub?: string; colour?: string; size?: number; suffix?: string }) {
  const v = value == null ? 0 : Math.max(0, Math.min(100, value));
  const data = [{ v }, { v: 100 - v }];
  return (
    <div className="flex items-center gap-3">
      <div style={{ width: size, height: size }} className="relative shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="v" cx="50%" cy="50%" innerRadius={size * 0.36} outerRadius={size * 0.48} startAngle={90} endAngle={-270} stroke="none" isAnimationActive>
              <Cell fill={colour}/><Cell fill={PALETTE.dim}/>
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center text-[20px] font-semibold font-mono">{value == null ? "—" : `${Math.round(value)}${suffix}`}</div>
      </div>
      <div><div className="text-[12px] font-medium text-text">{label}</div>{sub && <div className="text-[11px] text-text-muted">{sub}</div>}</div>
    </div>
  );
}

/** Horizontal bars, one per row, label on the left, value at the end. */
export function HBars({ rows, colour, height, money, max }: { rows: { name: string; value: number; colour?: string; sub?: string }[]; colour?: string; height?: number; money?: boolean; max?: number }) {
  const h = height ?? Math.max(60, rows.length * 30 + 10);
  const fmt = (v: number) => money ? (v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}m` : v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`) : String(v);
  return (
    <div style={{ height: h }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ top: 2, right: 44, bottom: 2, left: 4 }} barCategoryGap={6}>
          <XAxis type="number" hide domain={[0, max ?? "auto"]}/>
          <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11, fill: "#bdbdb6" }} axisLine={false} tickLine={false}/>
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={TIP} formatter={(v: any) => [fmt(Number(v)), ""]}/>
          <Bar dataKey="value" radius={[0, 4, 4, 0]} isAnimationActive>
            {rows.map((r, i) => <Cell key={i} fill={r.colour ?? colour ?? SERIES[i % SERIES.length]}/>)}
            <LabelList dataKey="value" position="right" formatter={(v: any) => fmt(Number(v))} style={{ fontSize: 11, fill: "#bdbdb6" }}/>
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Stacked columns by month — jobs starting, finishing, or whatever you hand it. */
export function Columns({ data, keys, height = 150, money }: { data: Record<string, any>[]; keys: { key: string; label: string; colour: string }[]; height?: number; money?: boolean }) {
  const fmt = (v: number) => money ? `$${Math.round(v / 1000)}k` : String(v);
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 6, right: 6, bottom: 0, left: -18 }} barCategoryGap={8}>
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#9aa0a6" }} axisLine={false} tickLine={false}/>
          <YAxis tick={{ fontSize: 10, fill: "#9aa0a6" }} axisLine={false} tickLine={false} allowDecimals={false} tickFormatter={money ? (v: any) => fmt(Number(v)) : undefined}/>
          <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={TIP} formatter={(v: any, n: any) => [fmt(Number(v)), keys.find(k => k.key === n)?.label ?? n]}/>
          {keys.map((k, i) => <Bar key={k.key} dataKey={k.key} stackId="a" fill={k.colour} radius={i === keys.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} isAnimationActive/>)}
        </BarChart>
      </ResponsiveContainer>
      <div className="flex gap-3 text-[10px] text-text-muted mt-1 px-1">{keys.map(k => <span key={k.key} className="inline-flex items-center gap-1"><i className="w-2 h-2 rounded-sm" style={{ background: k.colour }}/>{k.label}</span>)}</div>
    </div>
  );
}

/** A donut with a legend — bids won / lost / in play, defects by severity. */
export function Donut({ parts, size = 110, centre }: { parts: { name: string; value: number; colour: string }[]; size?: number; centre?: string }) {
  const total = parts.reduce((s, p) => s + p.value, 0);
  return (
    <div className="flex items-center gap-3">
      <div style={{ width: size, height: size }} className="relative shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={total ? parts : [{ name: "none", value: 1, colour: PALETTE.dim }]} dataKey="value" cx="50%" cy="50%" innerRadius={size * 0.34} outerRadius={size * 0.48} paddingAngle={total ? 2 : 0} stroke="none" isAnimationActive>
              {(total ? parts : [{ colour: PALETTE.dim }]).map((p, i) => <Cell key={i} fill={p.colour}/>)}
            </Pie>
            {total > 0 && <Tooltip contentStyle={TIP}/>}
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center text-[16px] font-semibold font-mono">{centre ?? total}</div>
      </div>
      <div className="space-y-0.5">{parts.map(p => <div key={p.name} className="text-[11px] flex items-center gap-1.5"><i className="w-2 h-2 rounded-sm" style={{ background: p.colour }}/><span className="text-text-muted">{p.name}</span><span className="font-mono text-text">{p.value}</span></div>)}</div>
    </div>
  );
}
